import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-upgrader-offset-slider',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './upgrader-offset-slider.component.html',
  styleUrl: './upgrader-offset-slider.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpgraderOffsetSliderComponent {
  offset = input<number>(0.5);
  chance = input<number>(0); // Win chance percentage (0-100)
  disabled = input<boolean>(false);

  offsetChange = output<number>();

  sliderTrack = viewChild<ElementRef>('sliderTrack');

  readonly isDragging = signal(false);

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (this.isDragging() && !this.disabled()) {
      event.preventDefault();
      this.updateOffset(event.clientX);
    }
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    this.isDragging.set(false);
  }

  @HostListener('document:touchmove', ['$event'])
  onTouchMove(event: TouchEvent) {
    if (this.isDragging() && !this.disabled()) {
      event.preventDefault();
      this.updateOffset(event.touches[0].clientX);
    }
  }

  @HostListener('document:touchend')
  onTouchEnd() {
    this.isDragging.set(false);
  }

  onSliderMouseDown(event: MouseEvent) {
    if (this.disabled()) return;
    event.preventDefault();
    this.isDragging.set(true);
    this.updateOffset(event.clientX);
  }

  onSliderTouchStart(event: TouchEvent) {
    if (this.disabled()) return;
    this.isDragging.set(true);
    this.updateOffset(event.touches[0].clientX);
  }

  private updateOffset(clientX: number) {
    const track = this.sliderTrack()?.nativeElement;
    if (!track) return;

    const rect = track.getBoundingClientRect();
    const x = clientX - rect.left;
    const width = rect.width;

    // Calculate offset (0 to 1)
    let newOffset = x / width;
    newOffset = Math.max(0, Math.min(1, newOffset));

    this.offsetChange.emit(newOffset);
  }

  /**
   * Get the width of the slider fill based on chance percentage
   */
  getFillWidth(): number {
    return Math.min(this.chance(), 100);
  }

  /**
   * Get the left position of the slider fill based on offset
   * The fill can move within the remaining space (100 - chance)
   */
  getFillPosition(): number {
    const chanceValue = Math.min(this.chance(), 100);
    const maxOffset = 100 - chanceValue;
    return this.offset() * maxOffset;
  }
}
